import React, { Fragment, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import styled from '@emotion/styled';
import { Decimal } from 'decimal.js';
import { themeGet } from 'styled-system';
import { useSelector } from 'react-redux';
import { Box, Flex, Icon, Text } from '@qga/roo-ui/components';
import { differenceInCalendarDays } from 'lib/date';
import { MIN_POINTS_AMOUNT } from 'config';
import usePointsConverters from 'hooks/points/usePointsConverters';
import { useDataLayer } from 'hooks/useDataLayer';
import { getQueryCheckIn, getQueryCheckOut, getQueryAdults, getQueryChildren, getQueryInfants } from 'store/router/routerSelectors';
import { getPropertyId } from 'store/property/propertySelectors';
import { getIsPointsPay } from 'store/ui/uiSelectors';
import Currency from 'components/Currency';
import CampaignPriceMessage from 'components/CampaignPriceMessage';
import OfferGuestsText from 'components/OfferGuestsText';
import OfferPointsPaySlider from './OfferPointsPaySlider';
import OfferCheckoutLink from './OfferCheckoutLink';
import OfferPayableAtProperty from './OfferPayableAtProperty';
import OfferPointsPaySliderUpdated from './OfferPointsPaySliderUpdated';
import OfferPriceCashAndPoints from './OfferPriceCashAndPoints';
import OfferDepositPayMessage from './OfferDepositPayMessage';
import { useBreakpoints } from 'hooks/useBreakpoints';
import CancellationTooltip from 'components/CancellationTooltip/CancellationTooltip';
import CancellationRefundModal from 'components/CancellationRefundModal';
import { useFeature } from '@optimizely/react-sdk';
import RoomsAvailabilityMessage from 'components/RoomsAvailabilityMessage';

const canPayWithPoints = ({ payableAtBooking, convertCashToPoints }) => {
  const payableAtBookingInPoints =
    payableAtBooking.total.currency === 'PTS'
      ? new Decimal(payableAtBooking.total.amount)
      : convertCashToPoints({ cash: payableAtBooking.total.amount });

  return payableAtBookingInPoints.greaterThanOrEqualTo(MIN_POINTS_AMOUNT);
};

const BoxWithBottom = styled.div`
  ${themeGet('mediaQueries.0')} {
    border-top: 0;
    padding-top: 0;
  }
`;

const BoxWithLeft = styled.div`
  border-left: 3px solid ${themeGet('colors.brand.primary')};
  padding-left: 8px;
  margin-bottom: 12px;
}
`;

const OfferPriceBox = memo(
  ({
    allocationsAvailable,
    charges,
    offerType,
    offerName,
    offerId,
    roomTypeId,
    roomName,
    onCashPaymentAmountChange,
    offerInstanceId,
    depositPay,
    cancellationPolicy,
  }) => {
    const propertyId = useSelector(getPropertyId);
    const isPointsPay = useSelector(getIsPointsPay);
    const isDepositPay = depositPay?.depositPayable;

    const checkIn = useSelector(getQueryCheckIn);
    const checkOut = useSelector(getQueryCheckOut);
    const totalNumberOfNights = differenceInCalendarDays(checkOut, checkIn);

    const adults = useSelector(getQueryAdults) || 0;
    const children = useSelector(getQueryChildren) || 0;
    const infants = useSelector(getQueryInfants) || 0;
    const totalGuests = adults + children + infants;

    const { convertCashToPoints } = usePointsConverters();
    const { total, payableAtProperty, payableAtBooking, totalCash } = charges;
    const currency = get(charges, 'total.currency');
    const isCurrencyCash = currency !== 'PTS';
    const isClassic = offerType === 'classic';
    const [isPointsRedemptionEnabled] = useFeature('qantas-hotels-redemption-experiment');

    const isShowingPointsPaySlider = isPointsPay && !isClassic && canPayWithPoints({ isClassic, payableAtBooking, convertCashToPoints });
    const isIncludedInTotal = isCurrencyCash;

    const { isLessThanBreakpoint } = useBreakpoints();
    const isMobile = isLessThanBreakpoint(0);

    const { emitInteractionEvent } = useDataLayer();
    const handleLinkClick = useCallback(() => {
      emitInteractionEvent({ type: 'Select Offer Button', value: `${offerName}` });
    }, [emitInteractionEvent, offerName]);

    const [isAvailableRoomsEnabled, variables] = useFeature('qantas-hotels-available-rooms-message');
    const availableRoomsMaxCutoff = variables?.['max-rooms-cutoff'] ?? 5;
    const showAvailableRooms =
      isAvailableRoomsEnabled && allocationsAvailable && allocationsAvailable > 0 && allocationsAvailable <= availableRoomsMaxCutoff;

    return (
      <Box data-testid="offer-price-box" pt={isShowingPointsPaySlider ? 0 : [4, 0]} width="100%">
        {isPointsRedemptionEnabled ? (
          <>
            <BoxWithBottom>
              <Box borderLeft={3} borderColor={'red'} data-testid="price-box-expanded" mb={3}>
                <OfferGuestsText guests={totalGuests} numberOfNights={totalNumberOfNights} pl={1} fontSize="16px" />
                <OfferPriceCashAndPoints totalCash={totalCash} totalPoints={total} isClassic={isClassic} pl={4} mt={2} />
                <Box ml={2}>
                  <CampaignPriceMessage
                    currency={total.currency}
                    offerType={offerType}
                    fallback
                    color={showAvailableRooms ? 'greys.steel' : undefined}
                  />
                </Box>
              </Box>
              <Box mb={3}>
                {showAvailableRooms && <RoomsAvailabilityMessage allocationsAvailable={allocationsAvailable} isOffer={true} />}
                {isMobile ? (
                  <Box mt={2}>
                    <CancellationRefundModal cancellationPolicy={cancellationPolicy} fontSize={['xs', 'sm']} />
                  </Box>
                ) : (
                  <CancellationTooltip
                    cancellationPolicy={cancellationPolicy}
                    fontSize={['xs', 'sm']}
                    data-testid="cancellation-tool-tip-desktop"
                  />
                )}
                {isDepositPay && <OfferDepositPayMessage depositPay={depositPay} />}
              </Box>

              {!isClassic && (
                <OfferPointsPaySliderUpdated
                  maxCash={parseFloat(totalCash?.amount)}
                  maxPoints={total.amount}
                  offerId={offerId}
                  offerInstanceId={offerInstanceId}
                  offerName={offerName}
                  // offerType={offerType} // TODO: ask
                  roomName={roomName}
                  roomTypeId={roomTypeId}
                  // payableAtProperty={payableAtProperty.total} // TODO: ask about the logic here
                  propertyId={propertyId}
                  onCashChange={onCashPaymentAmountChange}
                />
              )}
            </BoxWithBottom>
            {isClassic && (
              <OfferCheckoutLink
                propertyId={propertyId}
                roomTypeId={roomTypeId}
                offerId={offerId}
                offerName={offerName}
                roomName={roomName}
                mb={4}
                onClick={handleLinkClick}
              />
            )}
          </>
        ) : (
          <Fragment>
            <BoxWithLeft>
              <OfferGuestsText guests={totalGuests} numberOfNights={totalNumberOfNights} color={'greys.steel'} fontSize="sm" />
              <Flex flexDirection="row" justifyContent="flex-start" alignItems="baseline" flexWrap="wrap">
                <Flex alignItems="center" justifyContent={['flex-start', 'flex-end']} height="35px">
                  {isClassic && <Icon name="ribbon" mr="2" color="greys.charcoal" />}
                  <Currency
                    amount={total.amount}
                    currency={total.currency}
                    roundToCeiling
                    fontSize={32}
                    hideCurrency={true}
                    fontWeight="bold"
                    color="greys.charcoal"
                    data-testid="total-to-pay"
                    alignCurrency="superscript"
                  />
                  <Text pl={1} pb="10px" fontWeight={'bold'} data-testid="luxe-currency">
                    {currency}
                    {!isCurrencyCash && <sup>*</sup>}
                  </Text>
                </Flex>
              </Flex>
              <CampaignPriceMessage
                currency={total.currency}
                offerType={offerType}
                fallback
                color={showAvailableRooms ? 'greys.steel' : undefined}
              />
            </BoxWithLeft>
            <Box mb={3}>
              {showAvailableRooms && <RoomsAvailabilityMessage isOffer={true} allocationsAvailable={allocationsAvailable} />}
              {isMobile ? (
                <Box my={2}>
                  <CancellationRefundModal cancellationPolicy={cancellationPolicy} fontSize={['xs', 'sm']} mb={-2} />
                </Box>
              ) : (
                <Box data-testid="cancellation-tool-tip-desktop">
                  <CancellationTooltip cancellationPolicy={cancellationPolicy} fontSize={['xs', 'sm']} />
                </Box>
              )}
              {isDepositPay && <OfferDepositPayMessage depositPay={depositPay} />}
            </Box>
            <OfferPayableAtProperty payableAtProperty={payableAtProperty.total} isIncludedInTotal={isIncludedInTotal} />
            {isShowingPointsPaySlider && (
              <Fragment>
                <OfferPointsPaySlider
                  hasAvailability={true}
                  offerName={offerName}
                  offerType={offerType}
                  totalCash={totalCash || total} //totalCash is a temporary attribute passed by Ava in points mode due to the total in points not being the full total (payableAtBooking + payableAtProperty)
                  totalPointsAmount={total.amount}
                  payableAtProperty={payableAtProperty.total}
                  offerId={offerId}
                  offerInstanceId={offerInstanceId}
                  roomTypeId={roomTypeId}
                  propertyId={propertyId}
                  onCashPaymentAmountChange={onCashPaymentAmountChange}
                  roomName={roomName}
                  campaignPriceMessage={<CampaignPriceMessage currency={total.currency} offerType={offerType} />}
                  depositPay={depositPay}
                  isMobile={isMobile}
                  cancellationPolicy={cancellationPolicy}
                  isClassic={isClassic}
                />
              </Fragment>
            )}

            {!isShowingPointsPaySlider && !isMobile && (
              <OfferCheckoutLink
                propertyId={propertyId}
                roomTypeId={roomTypeId}
                offerId={offerId}
                offerName={offerName}
                roomName={roomName}
                data-testid="select-button-offer-price-box"
              />
            )}
          </Fragment>
        )}
      </Box>
    );
  },
);

OfferPriceBox.propTypes = {
  allocationsAvailable: PropTypes.number,
  charges: PropTypes.object.isRequired,
  offerType: PropTypes.string.isRequired,
  cancellationPolicy: PropTypes.shape({
    isNonrefundable: PropTypes.bool.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  depositPay: PropTypes.object.isRequired,
  roomTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  offerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  offerName: PropTypes.string.isRequired,
  offerInstanceId: PropTypes.string,
  roomName: PropTypes.string.isRequired,
  onCashPaymentAmountChange: PropTypes.func.isRequired,
};

OfferPriceBox.displayName = 'OfferPriceBox';

export default OfferPriceBox;
